import { SUPABASE_URL } from '../utils/supabase/url';
/**
 * Apple Push Notification Service — Frontend Client
 * Contract Group® — Quantis Platform
 *
 * Manages device registration, token lifecycle, and push notification
 * preferences via the /devices backend routes.
 */

import { projectId, publicAnonKey } from '../utils/supabase/info';
import { supabase } from '../utils/supabase/client';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b/devices`;

/**
 * Returns the caller-supplied token, or the live Supabase session token.
 * EVERY /devices route is JWT-gated: they expose and mutate device PII, and
 * send/send-batch push arbitrary caller-supplied content to someone's phone.
 * getRequestAccessContext rejects the anon key by design, so a call that falls
 * back to it is a guaranteed 401 rather than an unauthenticated success.
 */
async function resolveAccessToken(provided?: string): Promise<string | undefined> {
  if (provided && provided !== publicAnonKey) return provided;
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token ?? provided;
}

/** JWT-hardened device registration endpoint (derives user_id from Bearer token) */
const DEVICE_REGISTER_URL = `${SUPABASE_URL}/functions/v1/make-server-a476273b/device-register`;

/** Headers used for every request */
function headers(accessToken?: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken || publicAnonKey}`,
  };
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface RegisteredDevice {
  id: string;
  deviceToken: string;
  tokenPreview: string;
  platform: 'ios' | 'macos';
  apnsEnvironment?: 'development' | 'production';
  createdAt: string;
  /** Human-readable device name (e.g. "Gary's MacBook Pro"). Null when not stored. */
  deviceName: string | null;
  /** Device model identifier (e.g. "Mac16,1"). Null when not stored. */
  deviceModel: string | null;
  /** OS version string (e.g. "15.2"). Null when not stored. */
  osVersion: string | null;
  /** ISO-8601 timestamp of the last push interaction. Null when not yet recorded. */
  lastSeenAt: string | null;
  /** Last-known location string. Null when not recorded. */
  location: string | null;
}

export interface SendResult {
  success: boolean;
  suppressed?: boolean;
  reason?: string;
  sentCount: number;
  failedCount: number;
  total: number;
  usersTargeted?: number;
  invalidTokensRemoved?: number;
}

// ── Service class ────────────────────────────────────────────────────────────

class ApplePushServiceClass {
  private async fetchJsonWithRetry(
    input: RequestInfo | URL,
    init: RequestInit,
    attempts = 2,
    retryDelayMs = 900,
  ): Promise<Response> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        return await fetch(input, init);
      } catch (error) {
        lastError = error;
        if (attempt >= attempts) {
          break;
        }

        await new Promise((resolve) => window.setTimeout(resolve, retryDelayMs * attempt));
      }
    }

    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  /**
   * Register a device token for push notifications. Requires a signed-in user:
   * both endpoints below derive user_id from the Bearer token.
   *
   * Preferred path is the JWT-hardened /device-register edge function. The
   * /devices/register fallback remains for older servers that predate it, but
   * it no longer accepts a body-supplied userId, so it needs the same token.
   *
   * The `userId` parameter is now only used for logging and the legacy body.
   * It is not what the server trusts; passing someone else's id does nothing.
   */
  async registerDevice(
    userId: string,
    deviceToken: string,
    platform: 'ios' | 'macos' = 'ios',
    apnsEnvironment: 'development' | 'production' = 'production',
    accessToken?: string,
    meta?: { deviceName?: string; deviceModel?: string; osVersion?: string },
  ): Promise<{ success: boolean; device?: { id: string; platform: string; apnsEnvironment?: string; createdAt: string }; error?: string }> {
    try {
      // ── JWT-hardened path (preferred) ─────────────────────────────────
      // Uses /device-register which validates the Bearer JWT and derives
      // user_id server-side. Body uses snake_case to match the standalone
      // edge function schema: { device_token, platform }
      // Resolve the session token first, rather than only taking this path
      // when a caller happened to pass one. Callers that passed nothing fell
      // through to the anon-key fallback below, which trusts a body-supplied
      // userId - so a signed-in browser was using the weaker path for no
      // reason. The native client already sends its JWT and no userId.
      const resolvedToken = await resolveAccessToken(accessToken);
      if (resolvedToken && resolvedToken !== publicAnonKey) {
        console.log('[ApplePush] Using JWT-hardened /device-register endpoint');

        const body: Record<string, unknown> = {
          device_token: deviceToken,
          platform,
          apns_environment: apnsEnvironment,
        };
        if (meta?.deviceName)  body.device_name  = meta.deviceName;
        if (meta?.deviceModel) body.device_model = meta.deviceModel;
        if (meta?.osVersion)   body.os_version   = meta.osVersion;

        const response = await this.fetchJsonWithRetry(DEVICE_REGISTER_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${resolvedToken}`,
          },
          body: JSON.stringify(body),
        });

        const data = await response.json();

        if (!response.ok) {
          console.error('[ApplePush] JWT registration failed:', data.error);
          return { success: false, error: data.error };
        }

        // The JWT endpoint returns { ok: true, device: {...} } or
        // { message: 'Registered Successfully', device: {...} }
        const device = data.device;
        console.log('[ApplePush] JWT device registered:', device?.id);
        return {
          success: true,
          device: device ? {
            id: device.id,
            platform: device.platform,
            apnsEnvironment: device.apns_environment || device.apnsEnvironment,
            createdAt: device.created_at || device.createdAt,
          } : undefined,
        };
      }

      // ── Fallback path (older servers without /device-register) ───────
      // Reaching here means no session token resolved. /devices/register no
      // longer accepts a body-supplied userId, so this would 401. Say why
      // instead: a device cannot be bound to an identity we cannot prove, and
      // the caller should retry after sign-in rather than treat this as a
      // transient push failure.
      if (!resolvedToken || resolvedToken === publicAnonKey) {
        return {
          success: false,
          error: 'Not signed in - device registration needs an active session',
        };
      }

      console.log('[ApplePush] Using fallback /devices/register endpoint');

      const fallbackBody: Record<string, unknown> = { userId, deviceToken, platform, apnsEnvironment };
      if (meta?.deviceName)  fallbackBody.deviceName  = meta.deviceName;
      if (meta?.deviceModel) fallbackBody.deviceModel = meta.deviceModel;
      if (meta?.osVersion)   fallbackBody.osVersion   = meta.osVersion;

      const response = await this.fetchJsonWithRetry(`${API_BASE}/register`, {
        method: 'POST',
        headers: headers(resolvedToken),
        body: JSON.stringify(fallbackBody),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('[ApplePush] Registration failed:', data.error);
        return { success: false, error: data.error };
      }

      console.log('[ApplePush] Device registered:', data.device?.id);
      return { success: true, device: data.device };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[ApplePush] Registration error:', message);
      return { success: false, error: message };
    }
  }

  /**
   * Unregister a device token.
   */
  async unregisterDevice(
    userId: string,
    deviceToken: string,
    accessToken?: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_BASE}/unregister`, {
        method: 'POST',
        headers: headers(await resolveAccessToken(accessToken)),
        body: JSON.stringify({ userId, deviceToken }),
      });

      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { success: false, error: message };
    }
  }

  /**
   * Remove a device by its row ID.
   */
  async removeDevice(
    deviceId: string,
    accessToken?: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const token = await resolveAccessToken(accessToken);
      const response = await fetch(`${API_BASE}/${deviceId}`, {
        method: 'DELETE',
        headers: headers(token),
      });

      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { success: false, error: message };
    }
  }

  /**
   * List all registered devices for a user.
   */
  async listDevices(
    userId: string,
    accessToken?: string,
  ): Promise<{ devices: RegisteredDevice[]; error?: string }> {
    try {
      const token = await resolveAccessToken(accessToken);
      const response = await fetch(`${API_BASE}?userId=${encodeURIComponent(userId)}`, {
        headers: headers(token),
      });

      const data = await response.json();
      if (!response.ok) {
        return { devices: [], error: data.error };
      }

      // Normalise the raw response array, tolerantly mapping optional fields.
      // The server returns camelCase; fall back to null for any absent field.
      const devices: RegisteredDevice[] = (data.devices || []).map((d: Record<string, unknown>) => ({
        id:             d.id as string,
        deviceToken:    (d.deviceToken ?? d.device_token ?? '') as string,
        tokenPreview:   (d.tokenPreview ?? d.token_preview ?? '') as string,
        platform:       (d.platform ?? 'ios') as 'ios' | 'macos',
        apnsEnvironment:(d.apnsEnvironment ?? d.apns_environment ?? undefined) as 'development' | 'production' | undefined,
        createdAt:      (d.createdAt ?? d.created_at ?? '') as string,
        deviceName:     (d.deviceName ?? null) as string | null,
        deviceModel:    (d.deviceModel ?? null) as string | null,
        osVersion:      (d.osVersion ?? null) as string | null,
        lastSeenAt:     (d.lastSeenAt ?? null) as string | null,
        location:       (d.location ?? null) as string | null,
      }));

      return { devices };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { devices: [], error: message };
    }
  }

  /**
   * Send a push notification to all of a user's devices.
   */
  async sendToUser(
    userId: string,
    title: string,
    body: string,
    options?: {
      subtitle?: string;
      badge?: number;
      sound?: string;
      data?: Record<string, unknown>;
      category?: string;
      threadId?: string;
      sandbox?: boolean;
    },
    accessToken?: string,
  ): Promise<SendResult> {
    try {
      const response = await fetch(`${API_BASE}/send`, {
        method: 'POST',
        headers: headers(await resolveAccessToken(accessToken)),
        body: JSON.stringify({
          userId,
          title,
          body,
          ...options,
        }),
      });

      const data = await response.json();
      return {
        success: data.success ?? false,
        suppressed: data.suppressed ?? false,
        reason: data.reason,
        sentCount: data.sentCount ?? 0,
        failedCount: data.failedCount ?? 0,
        total: data.total ?? 0,
        usersTargeted: data.usersTargeted,
        invalidTokensRemoved: data.invalidTokensRemoved,
      };
    } catch (err) {
      console.error('[ApplePush] sendToUser error:', err);
      return { success: false, sentCount: 0, failedCount: 0, total: 0 };
    }
  }

  /**
   * Send a push notification to multiple users' devices.
   */
  async sendBatch(
    userIds: string[],
    title: string,
    body: string,
    options?: {
      subtitle?: string;
      badge?: number;
      sound?: string;
      data?: Record<string, unknown>;
      category?: string;
      threadId?: string;
      sandbox?: boolean;
    },
    accessToken?: string,
  ): Promise<SendResult> {
    try {
      const response = await fetch(`${API_BASE}/send-batch`, {
        method: 'POST',
        headers: headers(await resolveAccessToken(accessToken)),
        body: JSON.stringify({
          userIds,
          title,
          body,
          ...options,
        }),
      });

      const data = await response.json();
      return {
        success: data.success ?? false,
        suppressed: data.suppressed ?? false,
        reason: data.reason,
        sentCount: data.sentCount ?? 0,
        failedCount: data.failedCount ?? 0,
        total: data.total ?? 0,
        usersTargeted: data.usersTargeted,
        invalidTokensRemoved: data.invalidTokensRemoved,
      };
    } catch (err) {
      console.error('[ApplePush] sendBatch error:', err);
      return { success: false, sentCount: 0, failedCount: 0, total: 0 };
    }
  }

  /**
   * Send a test push notification to a specific device token.
   */
  async sendTest(
    deviceToken: string,
    sandbox?: boolean,
    accessToken?: string,
  ): Promise<{ success: boolean; apnsId?: string; reason?: string }> {
    try {
      const response = await fetch(`${API_BASE}/test`, {
        method: 'POST',
        headers: headers(await resolveAccessToken(accessToken)),
        body: JSON.stringify({ deviceToken, sandbox }),
      });

      return await response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { success: false, reason: message };
    }
  }
}

export const ApplePushService = new ApplePushServiceClass();
